import { useSelector, useDispatch } from "react-redux";
import {DataGrid, gridClasses, GridToolbar} from '@mui/x-data-grid';
import {DateTimePicker } from "@mui/x-date-pickers"
import {Box, Button, MenuItem, Select, TextField} from "@mui/material";
import { styled } from '@mui/material/styles';
import {stringSortComparator, parseIntSortComparator, formatCityStateZip} from "./utilities/string";
import {EMPTY_STATS, statsFromCollection} from "./utilities/stats";
import {INTENT_LIST_COLUMNS, TEMPLATE_FILENAME, parseIntentRows, mappingForFields,
    duplicateSerials, buildTemplateCsv} from "./utilities/intentList";
import { serialKey } from "./utilities/scanMatch";
import React, {useEffect, useState, useMemo} from "react";
import {getCollections, createCollection, uploadAssetsForCollection, getCollectionById, deleteCollection, updateCollection} from "./actions/collections";
import {getSerialNumbers} from "./actions/assets";
import {setCollections, addCollection} from "./reducers/collections";
import {useNavigate, useParams} from "react-router-dom";
import {getCustomers} from "./actions/customers";
import {setCustomers} from "./reducers/customers";
import {setStates} from "./reducers/states";
import {getStates} from "./actions/states";
import Dialog from "@mui/material/Dialog";
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import * as xlsx from "xlsx";

import {setAssets} from "./reducers/assets";
import {format} from "date-fns";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';

import Snackbar, { SnackbarCloseReason } from '@mui/material/Snackbar';
import SnackbarContent from '@mui/material/SnackbarContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import DescriptionIcon from '@mui/icons-material/Description';
import PostAddIcon from '@mui/icons-material/PostAdd';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { useRef } from 'react';

//https://mui.com/material-ui/react-dialog/#alerts

const columns = [
    { field: 'lot_number', headerName: 'Order Number', width: 150, editable: false, sortComparator: parseIntSortComparator },
    { field: 'start_time', headerName: 'Start Date', width: 150, editable: false, valueGetter: (params) => { try { return params.row.start_time.slice(0,10); } catch { return ''; } } },
    { field: 'end_time', headerName: 'End Date', width: 150, editable: false, valueGetter: (params) => { try { return params.row.end_time.slice(0,10); } catch { return ''; } } },
    { field: 'customer_name', headerName: 'Customer', width: 250, editable: false,
        valueGetter: (params) => params.row.customer?.customer_name ?? '',
        sortComparator: stringSortComparator },
    { field: 'contact_name', headerName: 'Contact Name', width: 250, editable: false,
        valueGetter: (params) => params.row.customer?.contact_name ?? '',
        sortComparator: stringSortComparator  },
    { field: 'address',
      headerName: 'Address',
      width: 400,
      editable: false,
        valueGetter: (params) =>
            `${params.row.customer?.address1 ?? ''} ${params.row.customer?.address2 ?? ''} ${params.row.customer?.city ?? ''}, ${params.row.state?.code || ''} ${params.row.customer?.zip_code ??''}`,
        sortComparator: stringSortComparator
    },
]



const pageSizeOptions = [20, 50, 100];

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
    [`&`]: { height: '850px' },
    [`& .${gridClasses.row}.even`]: {
        backgroundColor: '#F8F8F8'
    },
    [`& .${gridClasses.row}.selected`]: {
        backgroundColor: '#006225' // TODO make this match Compucycle branding guidelines
    } ,
    [`& .${gridClasses.row}.Mui-selected`]: {
        backgroundColor: '#006225', // TODO make this match Compucycle branding guidelines,
        color: 'white'
    },
    [`& .${gridClasses.row}.Mui-selected:hover`]: {
        backgroundColor: '#009f3c', // TODO make this match Compucycle branding guidelines,
        color: 'white'
    },
}));

// P1-16: the header-alias map that used to live here (three copies of it, two of them dead)
// has moved to utilities/intentList.js, which is now the only thing that reads column
// headers. A second copy here would leave two competing answers to "which headers do we
// accept?", and the copy that lost would be the one someone edits.


const CollectionManagement = (props = {}) => {
    const fileInputRef = useRef(null);
    const dispatch = useDispatch();
    const { id } = useParams();

    const [ isLoaded, setIsLoaded ] = useState(false);

    const collections = useSelector(state => state.collections.value );
    const customers = useSelector(state => state.customers.value );
    const auth = useSelector(state => state.auth.value );
    const states = useSelector(st => st.states.value );
    const [stats , setStats ] = useState(EMPTY_STATS);


    // P1-16: the preview grid now shows whichever columns the uploaded file actually
    // contained, keyed by database field rather than by the six hardcoded display names
    // the old positional parser forced on every file.
    const [ importFields, setImportFields ] = useState([]);
    const importColumns = importFields
        .map(field => INTENT_LIST_COLUMNS.find(column => column.field === field))
        .filter(Boolean);

    useEffect(() => {
        const fetchData = () => async (dispatch) => {
            if( customers.length === 0 ) {
                const updatedCustomers = await getCustomers(auth.token);
                dispatch(setCustomers(updatedCustomers.data));
            }
            if( collections.length === 0 ) {
                const updatedCollections = await getCollections(auth.token);
                dispatch(setCollections(updatedCollections.data));
            }
            if( states.length === 0 ) {
                const updatedStates = await getStates();
                dispatch(setStates(updatedStates.data));
            }
            setIsLoaded(true);
        }
        dispatch(fetchData());
    }, [dispatch,auth,collections,customers,states]);


    const navigate = useNavigate();
    const [selectionModel, setSelectionModel] = useState([]);

    const [ selectedCollection, setSelectedCollection ] = useState(id);
    const [ collectionsWithRelations, setCollectionsWithRelations ] = useState([]);
    const [ showNewCollectionDialog, setShowNewCollectionDialog ] = useState(false);

    const [openConfirm, setOpenConfirm] = React.useState(false);
    const [newCollection, setNewCollection ] = useState({});
    const [oldCollection, setOldCollection ] = useState({});
    const [openSnackbar, setSnackbar ] = useState(null);
    const [formErrors, setFormErrors] = useState(null);


    const [ creatingCollection, setCreatingCollection ] = useState(false);

    /*move states from asset management*/

    const [ xlsData, setXlsData ] = useState([]);
    const [ serialData, setSerialData ] = useState([]);
    // The duplicates, keyed the same way the importer and the server compare them, so the
    // preview can highlight every row involved rather than only the repeat.
    const duplicateKeys = useMemo(
        () => new Set((serialData || []).map(serialKey)),
        [serialData]);

    const [ showAssetCsv, setShowAssetCsv ] = useState(false);

    const [ editCollectionDialog, setEditCollectionDialog ] = useState(false);


    const [XlsUploadInProgress,setXlsUploadInProgress ] = useState(false);
    const [ defaultAssetType] = useState(10);
    const [ loading, setLoading ] = useState(false);
    const [ fileName, setFileName ] = useState('');
    const [ sheetName, setSheetName ] = useState('');
    const resetXlsUploadInterface = () => {
        setXlsData([]);
        setShowAssetCsv(false);
        //setShowXlsMapInterface(false);
    }

    //dee mo ibalik ung upload progress
    //sa node kana mag tangal ng mga hindi unique
    //serial lang dapat ang unique
    const uploadXlsToServer = async () => {
        setXlsUploadInProgress(true);
        // P1-16: rows arrive from handleFileChange already keyed by database field, and
        // the mapping describes only the columns the file actually had — so a file
        // without a Make column no longer claims to have one.
        const generatedMapping = mappingForFields(importFields);
        const mappedRows = xlsData;

        // P1-12: Item # is assigned from this array's order, so the uploaded file's row
        // order has to survive to the server. It previously did not: the rows were
        // split into blank-serial and non-blank, de-duplicated separately, then
        // recombined as [...emptySerials, ...uniqueData] — which moved every
        // blank-serial row to the front of the list. A file with a blank serial in the
        // middle of it would have produced Item # values matching no spreadsheet.
        //
        // P1-4 (2026-08-05): the de-duplication that used to happen here is GONE, not
        // moved. It silently dropped repeated serials before the POST, so the server
        // never saw a duplicate from this path and the requirement to *reject* such a
        // file (spec §1.1/§5.1) could not be enforced or even tested through the UI.
        //
        // Nothing replaces it, deliberately. The preview dialog already refuses to render
        // an Import button while `serialData` is non-empty, and `serialData` comes from the
        // same duplicateSerials() call in handleFileChange — so a re-check here would be
        // provably redundant. The real safety net is the server, which now rejects.
        const result = await uploadAssetsForCollection(auth.token, selectedCollectionId, { mapping: generatedMapping, assets: mappedRows, defaultAssetType });
        setSnackbar(result);

        const fetchData = async() => {
            if (selectedCollection != null) {
                const collection = await getCollectionById(selectedCollection.id, auth.token);
                dispatch(setAssets(collection.data.assets));
                setStats(statsFromCollection(collection));
            }
            setLoading(false);
        };
        await fetchData();
        await resetXlsUploadInterface();
        setXlsUploadInProgress(false);
    };

    const setNewCollectionCustomer = (value) => {

        //setNewCollection(Object.assign({}, newCollection, { customer_id: value }));
        let updatedCustomer = {id:0,customer_name:'',contact_name:'',phone_number:'',email_address:'',address1:'',address2:'',city:'',state_id:'',zip_code:'',contact_name2:'',phone_number2:'',email_address2:''};

        if(value >0){
            updatedCustomer = (customers.find(c => c.id === value) || { contact_name: "N/A"});
        }

        const copiedArray = structuredClone(updatedCustomer);
        Object.keys(copiedArray).map(function (key, index) {
            if (copiedArray[key] == null) {
                copiedArray[key] = "";
            }
        });
        //need to push the customer_id here
        copiedArray['start_time'] = newCollection.start_time;
        copiedArray['end_time'] = newCollection.end_time;
        copiedArray['lot_number'] = newCollection.lot_number;

        copiedArray['customer_id'] = value;

        setNewCollection(copiedArray)
        setNewCustomer(copiedArray);

        setFormErrors(null); //reset all form errors
    }

    const updateNewCollectionField = (field, value) => {
        const updatedCollection = Object.assign({}, newCollection, { [field]: value } );
        setNewCollection(updatedCollection);
    }

    const updateOldCollectionField = (field, value) => {
        const updatedCollection = Object.assign({}, oldCollection, { [field]: value } );
        //console.log(oldCollection)
        setOldCollection(updatedCollection);
    }

    const newCustomerDefault = {customer_name:'',contact_name:'',contact_name2:'',phone_number:'',phone_number2:'',email_address:'',email_address2:'',address1:'',address2:'',city:'',state_id:'',zip_code:''};

    const [newCustomer, setNewCustomer ] = useState(newCustomerDefault);

    const updateNewCustomerField = (field, value) => {
        const updatedCollection = Object.assign({}, newCollection, { [field]: value } );
        setNewCollection(updatedCollection);

        const updatedCustomer = Object.assign({}, newCustomer, { [field]: value } );
        setNewCustomer(updatedCustomer);

        if(formErrors) {
            let newFormErrors = formErrors.filter(item => item !== field);
            setFormErrors(newFormErrors);
        }
    }

    const handleClickOpen = () => {
        setOpenConfirm(true);
    };

    const handleClose = () => {
        setOpenConfirm(false);
    };

    const handleSnackbarClose = () =>{
        setSnackbar(null);
    }

    const handleCloseSelected =()=>{
        setSelectedCollection(null);
    }

    /* this is with sorting
    useEffect(() => {
        setCollectionsWithRelations(isLoaded ? collections
            .map( collection => {
                const cust =  customers.find( customer => customer.id === collection.customer_id);
                const the_state =states.find( s => s.id === cust.state_id);
                return Object.assign({}, collection, {
                    customer: cust,
                    state:  the_state } || { } );
            })
            .sort((a,b) => {
                if (a.customer.customer_name !== b.customer.customer_name) {
                    return a.customer.customer_name.localeCompare(b.customer.customer_name)
                }
                if (a.LotNumber !== b.LotNumber) {
                    return parseInt(a.lot_number,10) - parseInt(b.lot_number, 10);
                }
                return 0;
            }) : []);
    }, [collections, isLoaded,customers,states]);
     */

    useEffect(() => {
        setCollectionsWithRelations(isLoaded ? collections
                .map(collection => {
                    const cust = customers.find(customer => customer.id === collection.customer_id);
                    const the_state = states.find(s => s.id === cust?.state_id);
                    return { ...collection, customer: cust, state: the_state };
                })
            : []);
    }, [collections, isLoaded, customers, states]);


    useEffect(() => {
        const fetchData = async() => {
            if (selectedCollection != null) {
                const collection = await getCollectionById(selectedCollection.id, auth.token);
                setStats(statsFromCollection(collection));
            }
        };
        fetchData();
    }, [ selectedCollection,auth,collections]);


    const handleEditCollection = ()=>{

        let loadCollection = {
            id : selectedCollection.id,
            lot_number : selectedCollection.lot_number,
            //start_time:selectedCollection.start_time,
            start_time: dayjs(selectedCollection.start_time),
            end_time:dayjs(selectedCollection.end_time),
            customer_id : selectedCollection.customer_id,
            customer_name:selectedCollection.customer.customer_name,
            address1:selectedCollection.customer.address1,
            address2:selectedCollection.customer.address2,
            city:selectedCollection.customer.city,
            state_id:selectedCollection.customer.state_id,
            zip_code:selectedCollection.customer.zip_code,
            contact_name:selectedCollection.customer.contact_name,
            contact_name2:selectedCollection.customer.contact_name2,
            email_address:selectedCollection.customer.email_address,
            email_address2:selectedCollection.customer.email_address2,
            phone_number:selectedCollection.customer.phone_number,
            phone_number2:selectedCollection.customer.phone_number2}

        setOldCollection(loadCollection);
        setEditCollectionDialog(true);
        console.log(oldCollection);
    }



    const handleSelection = (newSelection) => {
        if (newSelection) {
            if( selectedCollection != null && newSelection[0] === selectedCollection.id) {
                setSelectionModel([]);
            } else {
                setSelectionModel(newSelection);
            }
        } else {
            setSelectionModel([]);
        }
    }

    const validCollection = (newCollection) => {
        let errors = [];
        if(!newCollection.start_time){
            errors.push('start_time');
        }
        if(!newCollection.customer_name){
            errors.push('customer_name');
        }

        if(!newCollection.lot_number){
            errors.push('lot_number');
        }

        if(errors){
            setFormErrors(errors);
        }

        return errors.length > 0 ? false : true;
        //return newCollection != null && newCollection.start_time != null && newCollection.customer_id != null && newCollection.customer_name !=null;
    }

    const rowClickListener = (
        params, // GridRowParams
        event, // MuiEvent<React.MouseEvent<HTMLElement>>
        details, // GridCallbackDetails
    ) => {
        let collection_id = 0;

        if( selectedCollection != null ) {
            if( selectedCollection.id === params.row.id ){ //pag nag deselect xa i null mo
                setSelectedCollection(null);
                collection_id = 0;
                //setOldCollection({});
            } else {
                setSelectedCollection(params.row);
                collection_id = params.row.id
                //etchData();

                //setOldCollection(params.row);
            }
            //console.log(selectedCollection);
        } else {
            setSelectedCollection(params.row);
            collection_id = params.row.id
            //fetchData();
            //setOldCollection(params.row);
            //console.log(selectedCollection);
        }


    };

    const collectionInProgress = selectedCollection == null || selectedCollection.EndDate == null || selectedCollection.EndDate === '' ;
    const selectedCollectionId = selectedCollection == null ? '' : selectedCollection.id;


    const customersSelectAdd = {id:0,customer_name:'Add New',contact_name:'',contact_name2:'',phone_number:'',phone_number2:'',email_address:'',email_address2:'',address1:'',address2:'',city:'',state_id:'',zip_code:''};
    const customersSelect = [customersSelectAdd].concat(customers);

    /*
    const handleFileChange = async (event) =>{

        setShowAssetCsv(true)
        setXlsUploadInProgress(true)
        const Serials = await getSerialNumbers(auth.token);
        console.log(Serials);

        const file = event.target.files[0];
        setFileName(file.name);
        const reader = new FileReader();
        reader.onload = function (e) {
            const xlsData = new Uint8Array(e.target.result);
            const workbook = xlsx.read(xlsData, {type: 'array'});
            const worksheet = workbook.Sheets[workbook.SheetNames];

            setSheetName(workbook.SheetNames[0]);

            const json = xlsx.utils.sheet_to_json(worksheet, {
                header: ['Asset Type', 'Asset Tag', 'Serial Number', 'Make', 'Model', 'Description'], defval: "",
            });

            // find and add duplicates to the Serials
            const seen = {};
            json.forEach(item => {
                const serial = item['Serial Number'];
                if (seen[serial]) {
                    Serials.push(serial);
                } else {
                    seen[serial] = true;
                }
            });

            setSerialData([...new Set(Serials)]);

            json.forEach(row => {
                Object.keys(row).forEach(key => {
                    const importIndex = importColumns.findIndex((x) => x.field === key);
                    if (importIndex !== -1) {
                        row[key] = importColumns[importIndex].postProcess(String(row[key]).trim());
                    }
                })
            });
            setXlsData([...json.slice(1)]);
            setXlsUploadInProgress(false)
        }
        reader.readAsArrayBuffer(file);
        event.target.value = '';
    };
  */
    // P1-16: read the file by its column HEADERS.
    //
    // This used to pass a `header` array to sheet_to_json, which tells SheetJS to ignore
    // the real header row and assign those six names to columns A-F by position. The
    // file's own headers were never read, so a correct list with its columns in a
    // different order -- or with any extra column in front of them, such as a file
    // exported from the Asset Management screen -- imported into the wrong fields
    // silently. Since P1-12 derives Item # from row order, the numbering was wrong too.
    const handleFileChange = async (event) =>{
        setShowAssetCsv(true)
        setXlsUploadInProgress(true)
        const file = event.target.files[0];
        setFileName(file.name);
        const reader = new FileReader();
        reader.onload = function (e) {
            const bytes = new Uint8Array(e.target.result);
            const workbook = xlsx.read(bytes, {type: 'array'});
            const sheet = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheet];

            setSheetName(sheet);

            // header: 1 gives a raw array-of-arrays, so the header row can be located and
            // matched by name instead of assumed to be row 0 in a fixed column order.
            const aoa = xlsx.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
            const parsed = parseIntentRows(aoa);

            if (parsed.headerRowIndex === -1) {
                setSnackbar({status: 'error', message:
                    'Oh Snap! No column headers found. Download the template to see the expected columns.'});
                setXlsData([]);
                setImportFields([]);
                setSerialData([]);
                setXlsUploadInProgress(false);
                return;
            }

            if (parsed.missingRequired.length > 0) {
                const labels = parsed.missingRequired
                    .map(field => INTENT_LIST_COLUMNS.find(c => c.field === field)?.header || field);
                setSnackbar({status: 'error', message:
                    `Oh Snap! Missing required column(s): ${labels.join(', ')}.`});
                setXlsData([]);
                setImportFields([]);
                setSerialData([]);
                setXlsUploadInProgress(false);
                return;
            }

            // Duplicates are compared case-insensitively and blanks are ignored -- the old
            // check hashed every blank serial to '' and reported the second blank row as a
            // duplicate, which blocked the whole import for a list that was perfectly valid.
            const duplicates = duplicateSerials(parsed.rows);
            if (duplicates.length > 0) {
                // Name them. The pink rows show which, but on a 500-row list that means
                // scrolling to find them; and the server's own rejection message names them
                // too, so the two now read the same.
                const named = duplicates.slice(0, 5).join(', ')
                    + (duplicates.length > 5 ? `, and ${duplicates.length - 5} more` : '');
                setSnackbar({status: 'error', message:
                    `Oh Snap! Intent list has ${duplicates.length} duplicate serial number`
                    + `${duplicates.length === 1 ? '' : 's'} and cannot be imported: ${named}`});
            } else if (parsed.unknownHeaders.length > 0) {
                // Not an error -- extra columns are normal in a client's own spreadsheet --
                // but say which ones were ignored rather than dropping them silently.
                setSnackbar({status: 'warning', message:
                    `Ignoring column(s) not used by the importer: ${parsed.unknownHeaders.join(', ')}.`});
            }

            setSerialData(duplicates);
            setImportFields(parsed.fields);
            setXlsData(parsed.rows);
            setXlsUploadInProgress(false);
        }
        reader.readAsArrayBuffer(file);
        event.target.value = '';
    };

    // P1-16: the client has been building intent lists from memory. Hand them the exact
    // columns the importer accepts.
    const downloadIntentListTemplate = () => {
        const blob = new Blob([buildTemplateCsv()], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = TEMPLATE_FILENAME;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    //console.log(customersSelect)

    return (
        <Box sx={{ height: '100vh', width: '100%' }}>
            <div style={{ paddingTop: 10}}>
                <Button style={{margin: 10}} onClick={() => setShowNewCollectionDialog(true)} variant="contained" color="primary">New Collection</Button>
            </div>

            {selectedCollection != null ? (
                <div id="the-collection" className="d-flex">
                    <div className="lefty" style={{width:'60%'}}>

                        <div className="d-flex">
                            <div>
                                <h2>ORDER# {selectedCollection.lot_number}</h2>

                                <div className="date">
                                    {selectedCollection.scan_complete === true ? (
                                        <>
                                            {selectedCollection.start_time?.slice(0, 10)} - {selectedCollection.scan_complete_date?.slice(0, 10)}
                                            <span>SCAN COMPLETED</span>
                                        </>
                                    ) : (
                                        <>
                                            {selectedCollection.start_time?.slice(0, 10)} - {collectionInProgress ? 'In Progress' : selectedCollection.end_time?.slice(0, 10)}
                                        </>
                                    )}
                                </div>

                            </div>
                        </div>
                        <div className="d-flex w-100" style={{gap:'15px'}}>
                            <div className="col-4">
                                <h3>{selectedCollection.customer.customer_name || ''}</h3>
                                <address>
                                    {selectedCollection.customer.address1 || ''}<br/>
                                    {(selectedCollection.customer.address2 != null && selectedCollection.customer.address2.trim() !== '') ?
                                        <>{`${selectedCollection.customer.address2}`}<br/></> : null}
                                    {formatCityStateZip(selectedCollection.customer.city, selectedCollection.state?.code, selectedCollection.customer.zip_code)}<br/>

                                </address>
                                <div style={{display: "flex", flexDirection: "row", gap: 4, marginTop: 15}}>

                                    <Button variant="contained" color="primary" disabled={selectedCollection == null} onClick={handleEditCollection}>Edit</Button>
                                    <Button variant="contained" color="error" disabled={selectedCollection == null} onClick={handleClickOpen}>Delete</Button>

                                </div>

                            </div>
                            <div className="col-4">
                                <h4>Onsite Contact 1:</h4>
                                <address>
                                    {selectedCollection.customer.contact_name}<br/>
                                    {selectedCollection.customer.email_address}<br/>
                                    {selectedCollection.customer.phone_number}
                                </address>
                            </div>
                            <div className="col-4">
                                <h4>Onsite Contact 2</h4>
                                <address>{selectedCollection.customer.contact_name2}<br/>
                                    {selectedCollection.customer.email_address2}<br/>
                                    {selectedCollection.customer.phone_number2}</address>

                            </div>
                        </div>

                    </div>


                    <div className="righty" style={{width: '40%', position: 'relative'}}>
                        <div style={{'position': 'absolute', 'right': '-10px','top':'-10px'}}>
                            <IconButton
                                size="small"
                                aria-label="close"
                                color="inherit"
                                onClick={handleCloseSelected}>
                                <CloseIcon fontSize="small"/>
                            </IconButton>
                        </div>


                        <div className="d-flex" style={{width: '100%', justifyContent: "end", display: "flex"}}>
                            <div className="boxed-content-stats">
                                <h2>{stats.expected}</h2>
                                <p><strong>Expected</strong> <br/>Asset Count</p>
                            </div>
                            <div className="boxed-content-stats">
                                <h2>{stats.matched}</h2>
                                <p><strong>Matched</strong> <br/> Asset Count</p>
                            </div>
                            <div className="boxed-content-stats">
                                <h2>{stats.unscanned}</h2>
                                <p><strong>Unscanned</strong> <br/>Asset Count</p>
                            </div>

                            <div className="boxed-content-stats">
                                <h2>{stats.unexpected}</h2>
                                <p><strong>Unexpected</strong> <br/>Asset Count</p>
                            </div>

                        </div>


                        <div style={{width: '100%', justifyContent: "end", display: "flex", marginTop: '20px'}}>

                            <Button style={{margin: 10}} variant="contained" color="primary" disabled={selectedCollection == null} onClick={() => navigate(`/CollectionReports/${selectedCollection.id}`)}>View Report</Button>

                            <Button style={{margin: 10}} variant="contained" color="primary" disabled={selectedCollection == null} onClick={() => navigate(`/AssetManagement/${selectedCollection.id}`)}>View Assets</Button>

                            {/* P1-16: the expected columns were previously undocumented in the app —
                                the client built intent lists from memory. Outlined rather than
                                contained so it reads as a helper next to the primary action. */}
                            <Button tabIndex={-1} onClick={downloadIntentListTemplate}
                                    style={{margin: 10}} variant="outlined" color="primary"
                                    title="Download a CSV with the columns the importer accepts">
                                Download Template
                            </Button>

                            <Button tabIndex={-1} onClick={() => {
                                fileInputRef.current.click()
                            }} style={{margin: 10}} variant="contained" color="primary" disabled={false}>Import Intent List</Button>


                        </div>

                        <input ref={fileInputRef} type="file" onChange={handleFileChange} style={{display: 'none'}} accept=".xlsx,.csv"/>


                    </div>


                </div>) : null}

            <StripedDataGrid
                slots={{toolbar: GridToolbar}}
                getRowClassName={(params) => params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'}
                onRowSelectionModelChange={handleSelection}
                rowSelectionModel={selectionModel}
                rows={collectionsWithRelations}
                columns={columns}
                initialState={{
                    pagination: {
                        paginationModel: {
                            pageSize: 20,
                        },
                    },
                }}
                onRowClick={rowClickListener}
                density="compact"
                pageSizeOptions={pageSizeOptions}/>

            {!isLoaded ? (
                <div style={{position: "fixed", top: 0, bottom: 0, left: 0, right: 0, backgroundColor: "#00000022", display: "flex", justifyContent: "center", alignItems: "center"}}>
                    <div className="lds-roller">
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                    </div>
                </div>
            ) : null}

            {showNewCollectionDialog ? (
                <div style={{position: "fixed", top: 0, bottom: 0, left: 0, right: 0, backgroundColor: "#00000022", display: "flex", justifyContent: "center", alignItems: "center"}}>
                    <div style={{width: "800px", display: "flex", flexDirection: "column", background: "white", position: "relative"}}>
                        <div style={{fontSize: 20, fontWeight: "bold", padding: 20, alignSelf: "center", display: "flex"}}>New Collection</div>
                        <div style={{display: "flex", justifyContent: "start", flexDirection: "column", padding: 10}}>

                            <div style={{display: "flex", flexDirection: "row"}}>
                                <TextField
                                    style={{marginLeft: 10, marginTop: 10, marginRight: 10, width: "100%"}}
                                    value={newCollection.lot_number || ''}
                                    label="Order Number"
                                    error={(formErrors && formErrors.includes('lot_number'))}
                                    onChange={(e) => updateNewCollectionField('lot_number', e.target.value)}>
                                </TextField>
                            </div>

                            <div style={{display: "flex", gap: 10, flexDirection: "row", marginLeft: 10, marginRight: 10, marginTop: 10}}>


                                <DateTimePicker
                                    style={{width:'100%'}}
                                    label="Service Date"
                                    value={(formErrors && formErrors.includes('start_time') ? '' : newCollection.start_time)}
                                    onChange={(newValue) => updateNewCollectionField("start_time", newValue)}/>

                                <DateTimePicker
                                    style={{width:'100%'}}
                                    label="End Date"
                                    value={newCollection.end_time}
                                    onChange={(newValue) => updateNewCollectionField("end_time", newValue)}/>
                            </div>
                            <div style={{display: "flex", flexDirection: "row"}}>
                                <TextField
                                    style={{marginLeft: 10, marginTop: 10, marginRight: 10, width: "100%"}}
                                    id="customer-select"
                                    value={newCollection.customer_id || ''}
                                    label="Select Customer"
                                    select
                                    onChange={(e) => setNewCollectionCustomer(e.target.value)}>
                                    {customersSelect.map(c => <MenuItem key={c.id} value={c.id}>{c.customer_name}</MenuItem>)}
                                </TextField>
                            </div>


                            <div style={{padding: "0px 10px"}}>
                                <h4 style={{lineHeight: 1}}>Customer Info</h4>
                                <TextField
                                    onChange={(e) => updateNewCustomerField('customer_name', e.target.value)}
                                    style={{width: "100%"}}
                                    label="Customer Name"
                                    variant="outlined"
                                    error={(formErrors && formErrors.includes('customer_name'))}
                                    value={newCustomer.customer_name}/>

                                <TextField onChange={(e) => updateNewCustomerField('address1', e.target.value)} style={{marginTop: 10, width: "100%"}} id="customer-address1" label="Address" variant="outlined" value={newCustomer.address1}/>

                                <TextField onChange={(e) => updateNewCustomerField('address2', e.target.value)} style={{marginTop: 10, width: "100%"}} id="customer-address1" label="Address 2" variant="outlined" value={newCustomer.address2}/>
                            </div>

                            <div style={{display: "flex", flexDirection: "row", gap: 10, marginTop: 10, padding: '0px 10px'}}>
                                <TextField onChange={(e) => updateNewCustomerField('city', e.target.value)} style={{width: "100%"}} id="customer-city" label="City" variant="outlined" value={newCustomer.city}/>
                                <TextField
                                    style={{width: '100%'}}
                                    id="customer-state"
                                    value={newCustomer.state_id}
                                    label="State"
                                    select
                                    variant="outlined"
                                    onChange={(e) => updateNewCustomerField('state_id', e.target.value)}>
                                    {states.map(st => <MenuItem key={st.id} value={st.id}>{st.name}</MenuItem>)}
                                </TextField>

                                <TextField onChange={(e) => updateNewCustomerField('zip_code', e.target.value)} id="customer-zip" style={{width: '100%'}} type="number" label="ZIP Code" variant="outlined" value={newCustomer.zip_code}/>
                            </div>

                            <div style={{padding: '0 10px'}}>
                                <h4 style={{lineHeight: 1}}>Onsite Contact #1</h4>
                                <div style={{display: "flex", flexDirection: "row", gap: 10, width: '100%'}}>
                                    <TextField onChange={(e) => updateNewCustomerField('contact_name', e.target.value)} style={{width: '500px'}} id="customer-contact-name" label="Contact Name" variant="outlined" value={newCustomer.contact_name}/>
                                    <TextField onChange={(e) => updateNewCustomerField('phone_number', e.target.value)} style={{width: '380px'}} id="customer-contact-phone" label="Contact Phone" variant="outlined" value={newCustomer.phone_number}/>
                                    <TextField onChange={(e) => updateNewCustomerField('email_address', e.target.value)} style={{width: '100%'}} id="customer-contact-email" label="Contact Email" variant="outlined" value={newCustomer.email_address}/>
                                </div>
                            </div>

                            <div style={{padding: '0 10px'}}>
                                <h4 style={{lineHeight: 1}}>Onsite Contact #2</h4>
                                <div style={{display: "flex", flexDirection: "row", gap: 10}}>
                                    <TextField onChange={(e) => updateNewCustomerField('contact_name2', e.target.value)} style={{width: '500px'}} id="customer-contact-name" label="Contact Name" variant="outlined" value={newCustomer.contact_name2}/>
                                    <TextField onChange={(e) => updateNewCustomerField('phone_number2', e.target.value)} style={{width: '380px'}} id="customer-contact-phone" label="Contact Phone" variant="outlined" value={newCustomer.phone_number2}/>
                                    <TextField onChange={(e) => updateNewCustomerField('email_address2', e.target.value)} style={{width: '100%'}} id="customer-contact-email" label="Contact Email" variant="outlined" value={newCustomer.email_address2}/>
                                </div>
                            </div>


                            <div style={{padding: 20, display: "flex", flexDirection: "row-reverse"}}>

                                <Button style={{margin: 10}} onClick={async () => {

                                    if (!validCollection(newCollection)) {
                                        return;
                                    }
                                    setCreatingCollection(true);
                                    const result = await createCollection(auth.token, newCollection);

                                    if (result.status === "success") {
                                        console.log(newCollection);

                                        const date = new Date(newCollection.start_time);
                                        const isoString = date.toISOString();
                                        dispatch(addCollection({ ...newCollection,
                                                                 id: result.id,
                                                                 start_time:new Date(newCollection.start_time).toISOString(),
                                                                 end_time:new Date(newCollection.end_time).toISOString()
                                                                  }));

                                        //dispatch(addCollection(Object.assign({}, newCollection, {id: result.id})));
                                    } else {
                                        console.log("Error saving asset: " + JSON.stringify(result.error));
                                    }

                                    const updatedCustomers = await getCustomers(auth.token);
                                    dispatch(setCustomers(updatedCustomers.data));

                                    const updatedCollections = await getCollections(auth.token);
                                    const array = updatedCollections.data;

                                    const item = array.find(obj => obj.id === result.id);
                                    if (item) {
                                        array.splice(array.indexOf(item), 1);
                                        array.unshift(item);
                                    }



                                    dispatch(setCollections(array));
                                    setSnackbar(result);
                                    setCreatingCollection(false);
                                    setShowNewCollectionDialog(false);
                                    setNewCollection({});
                                    setNewCustomer(newCustomerDefault);

                                }} variant="contained" color="primary">Save</Button>


                                <Button style={{margin: 10}} onClick={() => {
                                    setShowNewCollectionDialog(false);
                                    setNewCollection({});
                                    setNewCustomer(newCustomerDefault);
                                    setFormErrors(null);
                                }} variant="contained" color="error">Cancel</Button>
                                {creatingCollection ? (
                                    <div style={{position: "fixed", top: 0, bottom: 0, left: 0, right: 0, backgroundColor: "#00000022", display: "flex", justifyContent: "center", alignItems: "center"}}>
                                        <div className="lds-roller">
                                            <div></div>
                                            <div></div>
                                            <div></div>
                                            <div></div>
                                            <div></div>
                                            <div></div>
                                            <div></div>
                                            <div></div>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
            {showAssetCsv ?
                <Dialog open={showAssetCsv} maxWidth='xl' fullWidth={true}>
                    <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>

                        <div style={{display: 'flex', padding: '15px 20px', justifyContent: 'space-between', backgroundColor: '#F0F0F0', marginBottom: 0}}>
                            <div style={{fontSize: 14, display: 'flex', gap: 5, alignItems: 'center'}}>
                                <span style={{fontWeight: 700, fontSize: 18, paddingRight: 20}}>DATA PREVIEW</span>
                                <span style={{fontSize: 14, display: 'flex', gap: 5, alignItems: 'center', marginRight: 10}}> {<PostAddIcon sx={{fontSize: 12}}/>} {fileName}</span>
                                <span style={{fontSize: 14, display: 'flex', gap: 5, alignItems: 'center'}}> {<PostAddIcon sx={{fontSize: 12}}/>} {sheetName}</span>
                            </div>
                            <div style={{fontSize: 12, lineHeight: 1}}>
                                <a href="" onClick={(e) => {
                                    e.preventDefault();
                                    resetXlsUploadInterface();
                                }}>{<CloseIcon sx={{fontSize: 25, fontWeight: 800}}/>}</a>

                            </div>
                        </div>

                        {xlsData != null && xlsData.length > 0 ? (
                            <>
                                <div style={{maxHeight: '500px', overflow: 'auto', width: '100%', marginTop: 0}}>
                                    <table id="import-table" style={{padding: 0, width: '100%', borderCollapse: 'collapse', margin: '0'}}>
                                        <thead key={'thead'}>
                                        <tr>
                                            {/* P1-12: Item # comes from the file's row order, so show it in the
                                                preview — the tester can then confirm the numbering before importing. */}
                                            <th style={{backgroundColor: '#F8F8F8', position: "sticky", top: 0, textAlign: 'left', paddingTop: 15, paddingBottom: 15, textTransform: 'uppercase', color: '#1C3627'}}>Item #</th>
                                            {importColumns.map(x => <th key={x.field} style={{backgroundColor: '#F8F8F8', position: "sticky", top: 0, textAlign: 'left', paddingTop: 15, paddingBottom: 15, textTransform: 'uppercase', color: '#1C3627'}}>{x.header}</th>)}
                                        </tr>
                                        </thead>
                                        <tbody key={'tbody'}>
                                        {xlsData.map((row, index) => {
                                            // P1-4: matched through serialKey so BOTH spellings of a
                                            // whitespace-only difference highlight, not just the second one.
                                            // A plain string compare left the first occurrence looking innocent.
                                            return (
                                                <tr key={index} style={{backgroundColor: (duplicateKeys.has(serialKey(row.serial_number)) ? '#FFEDED' : (index % 2 ? 'white' : '#F8F8F8'))}}>
                                                    <td style={{borderTop: '1px dotted grey', color: '#6b6b6b'}}>{index + 1}</td>
                                                    {importColumns.map(column => <td key={column.field} style={{borderTop: '1px dotted grey'}}>{row[column.field]}</td>)}
                                                </tr>
                                            );
                                        })}
                                        </tbody>
                                    </table>
                                </div>
                            </>) : null}

                        {XlsUploadInProgress ?
                            <Box sx={{width: '100%'}}>
                                <LinearProgress/>
                            </Box>
                            : null
                        }

                        <div style={{display: 'flex', padding: 5, alignItems: 'center', gap: 10}}>
                            {serialData.length === 0 ? (
                                <>
                                    <Button
                                        tabIndex={-1}
                                        disabled={!xlsData?.length}
                                        onClick={uploadXlsToServer}
                                        style={{marginLeft: 10, marginTop: 10, marginBottom: 10, width: 200, height: 50}}
                                        variant="contained"
                                        color="primary"
                                    >
                                        Import
                                    </Button>
                                    <button
                                        onClick={() => fileInputRef.current.click()}
                                        style={{background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: '#004419'}}
                                    >
                                        OR Upload New File
                                    </button>
                                </>
                            ) : (
                             <Button
                                    variant="contained"
                                    color="primary"
                                    style={{marginLeft: 10, marginTop: 10, marginBottom: 10, width: 200, height: 50}}
                                    onClick={() => fileInputRef.current.click()}>
                            Upload New File
                           </Button>
                        )}
                        </div>
                    </div>
                </Dialog> : null}

            {openSnackbar ?
                <Snackbar open={true} onClose={handleSnackbarClose} anchorOrigin={{vertical: 'top', horizontal: 'right'}} autoHideDuration={6000}>
                    <Alert
                        onClose={handleSnackbarClose}
                        severity={openSnackbar.status}
                        sx={{width: '100%'}}>
                        {openSnackbar.message}
                    </Alert>
                </Snackbar>
                : null}


            <Dialog open={editCollectionDialog} maxWidth='md' fullWidth={true}>
                <DialogTitle> Edit Collection </DialogTitle>
                <DialogContent>
                    <div style={{padding: "0px 10px"}}>
                        <h4 style={{lineHeight: 1}}>Service Dates</h4>

                        <div>

                            <TextField onChange={(e) => updateOldCollectionField('lot_number', e.target.value)}
                                       style={{width: "100%"}}
                                       label="Order Number" variant="outlined" value={oldCollection.lot_number}/>

                        </div>

                        <div style={{display: "flex", gap: 10, flexDirection: "row", marginRight: 10, marginTop: 10}}>

                            <DateTimePicker
                                label="Service Date"
                                value={oldCollection.start_time}
                                onChange={(newValue) => updateOldCollectionField('start_time', newValue)}/>
                            <DateTimePicker
                                label="End Date"
                                value={oldCollection.end_time}
                                onChange={(newValue) => updateOldCollectionField('end_time', newValue)}/>
                        </div>
                    </div>

                    <div style={{padding: "0px 10px"}}>
                        <h4 style={{lineHeight: 1}}>Customer Info</h4>

                        <TextField onChange={(e) => updateOldCollectionField('customer_name', e.target.value)} style={{width: "100%"}} id="customer-name" label="Customer Name" variant="outlined" value={oldCollection.customer_name}/>


                        <TextField onChange={(e) => updateOldCollectionField('address1', e.target.value)} style={{marginTop: 10, width: "100%"}} id="customer-address1" label="Address" variant="outlined" value={oldCollection.address1}/>

                        <TextField onChange={(e) => updateOldCollectionField('address2', e.target.value)} style={{marginTop: 10, width: "100%"}} id="customer-address1" label="Address 2" variant="outlined" value={oldCollection.address2}/>
                    </div>

                    <div style={{display: "flex", flexDirection: "row", gap: 10, marginTop: 10, padding: '0px 10px'}}>
                        <TextField onChange={(e) => updateNewCustomerField('city', e.target.value)} style={{width: "100%"}} id="customer-city" label="City" variant="outlined" value={newCustomer.city}/>
                        <TextField
                            style={{width: '100%'}}
                            id="customer-state"
                            value={newCustomer.state_id}
                            label="State"
                            select
                            variant="outlined"
                            onChange={(e) => updateNewCustomerField('state_id', e.target.value)}>
                            {states.map(st => <MenuItem key={st.id} value={st.id}>{st.name}</MenuItem>)}
                        </TextField>

                        <TextField onChange={(e) => updateNewCustomerField('zip_code', e.target.value)} id="customer-zip" style={{width: '100%'}} type="number" label="ZIP Code" variant="outlined" value={newCustomer.zip_code}/>
                    </div>
                    <div style={{padding: '0 10px'}}>
                        <h4 style={{lineHeight: 1}}>Onsite Contact #1</h4>
                        <div style={{display: "flex", flexDirection: "row", gap: 10, width: '100%'}}>
                            <TextField onChange={(e) => updateOldCollectionField('contact_name', e.target.value)} style={{width: '500px'}} id="customer-contact-name" label="Contact Name" variant="outlined" value={oldCollection.contact_name}/>
                            <TextField onChange={(e) => updateOldCollectionField('phone_number', e.target.value)} style={{width: '380px'}} id="customer-contact-phone" label="Contact Phone" variant="outlined" value={oldCollection.phone_number}/>
                            <TextField onChange={(e) => updateOldCollectionField('email_address', e.target.value)} style={{width: '100%'}} id="customer-contact-email" label="Contact Email" variant="outlined" value={oldCollection.email_address}/>
                        </div>
                    </div>

                    <div style={{padding: '0 10px'}}>
                        <h4 style={{lineHeight: 1}}>Onsite Contact #2</h4>
                        <div style={{display: "flex", flexDirection: "row", gap: 10}}>
                            <TextField onChange={(e) => updateOldCollectionField('contact_name2', e.target.value)} style={{width: '500px'}} id="customer-contact-name" label="Contact Name" variant="outlined" value={oldCollection.contact_name2}/>
                            <TextField onChange={(e) => updateOldCollectionField('phone_number2', e.target.value)} style={{width: '380px'}} id="customer-contact-phone" label="Contact Phone" variant="outlined" value={oldCollection.phone_number2}/>
                            <TextField onChange={(e) => updateOldCollectionField('email_address2', e.target.value)} style={{width: '100%'}} id="customer-contact-email" label="Contact Email" variant="outlined" value={oldCollection.email_address2}/>
                        </div>
                    </div>


                </DialogContent>
                <DialogActions style={{paddingBottom: 20, paddingRight: 20}}>
                    <Button onClick={() => {
                        setEditCollectionDialog(false);
                        setFormErrors(null);
                    }}>Cancel</Button>
                    <Button variant="contained" color="primary" autoFocus onClick={ async ()=>{
                        //console.log(oldCollection);
                        const result = await updateCollection(auth.token, oldCollection);
                        setSnackbar(result);

                        const updatedCustomers = await getCustomers(auth.token);
                        dispatch(setCustomers(updatedCustomers.data));

                        const updatedCollections = await getCollections(auth.token);
                        dispatch(setCollections(updatedCollections.data));

                        //much better if you update this pero wag na
                        setSelectedCollection(null);
                        setEditCollectionDialog(false);



                    }}>Update</Button>
                </DialogActions>
            </Dialog>


            <Dialog
                open={openConfirm}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description">

                <DialogTitle id="alert-dialog-title">
                    {"Confirm Delete"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this Collection and all Assets related to this collection? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions style={{paddingBottom: 20, paddingRight: 20}}>
                    <Button onClick={handleClose}>No! Cancel </Button>
                    <Button variant="contained" color="primary" autoFocus onClick={async () => {

                        const result = await deleteCollection(selectedCollection.id, auth.token);
                        setSnackbar(result);
                        //clear selected selections since it has been deleted
                        setSelectedCollection(null);

                        /* no need to redispatch customers?
                        const updatedCustomers = await getCustomers(auth.token);
                        dispatch(setCustomers(updatedCustomers.data));
                         */

                        const updatedCollections = await getCollections(auth.token);
                        dispatch(setCollections(updatedCollections.data));
                        setOpenConfirm(false)

                    }}>Yes. Delete the collection</Button>
                </DialogActions>
            </Dialog>


        </Box>
    )
}

export default CollectionManagement;
